import { useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import { TextBlock } from '../../Atoms/Text';
import { ConfirmationModal } from '../../Atoms/ConfirmationModal';
import { McpAccessCreateForm } from './McpAccessCreateForm';
import { McpAccessProfilesTable } from './McpAccessProfilesTable';
import { McpAccessTokenRevealModal } from './McpAccessTokenRevealModal';
import { McpAccessEditDialog } from './McpAccessEditDialog';
import {
  fetchMcpAccessProfiles,
  createMcpAccessProfile,
  rotateMcpAccessProfile,
  updateMcpAccessProfile,
  revokeMcpAccessProfile,
  getMcpAccessProfilesSubject,
  getMcpAccessLoadingSubject,
  getMcpAccessListErrorSubject,
  McpAccessProfile,
  McpAccessProfileInput,
  McpAccessProfileWithToken,
  McpAccessScope
} from '../../../store/McpAccess';
import { getUserSubject, User } from '../../../store/User';
import { allowedMcpScopesForRole } from '../../../utils/mcpAccess';

export const McpAccessSection = () => {
  const [profiles, setProfiles] = useState<McpAccessProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [revealProfile, setRevealProfile] = useState<McpAccessProfileWithToken | null>(null);
  const [revealNotice, setRevealNotice] = useState<string | null>(null);
  const [editProfile, setEditProfile] = useState<McpAccessProfile | null>(null);
  const [rotateTarget, setRotateTarget] = useState<McpAccessProfile | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<McpAccessProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState<User>();

  useEffect(() => {
    const profilesSub = getMcpAccessProfilesSubject().subscribe(setProfiles);
    const loadingSub = getMcpAccessLoadingSubject().subscribe(setLoading);
    const errorSub = getMcpAccessListErrorSubject().subscribe(setListError);
    const userSub = getUserSubject().subscribe(setUser);
    fetchMcpAccessProfiles();
    return () => {
      profilesSub.unsubscribe();
      loadingSub.unsubscribe();
      errorSub.unsubscribe();
      userSub.unsubscribe();
    };
  }, []);

  const allowedScopes = useMemo(() => allowedMcpScopesForRole(user?.role), [user?.role]);

  const reveal = (profile: McpAccessProfileWithToken, notice: string | null = null) => {
    setRevealNotice(notice);
    setRevealProfile(profile);
  };

  const closeReveal = () => {
    setRevealProfile(null);
    setRevealNotice(null);
  };

  const handleCreate = async (input: McpAccessProfileInput): Promise<boolean> => {
    setSubmitting(true);
    try {
      const created = await createMcpAccessProfile(input);
      if (created) reveal(created);
      return !!created;
    } finally {
      setSubmitting(false);
    }
  };

  const handleRotateConfirm = async () => {
    // Rotation re-mints at the profile's existing scope; never do so for a scope this role can't hold
    // (the row's rotate button is already disabled — this guards the store call regardless).
    if (!rotateTarget || !allowedScopes.includes(rotateTarget.scope)) return;
    setSubmitting(true);
    try {
      const rotated = await rotateMcpAccessProfile(rotateTarget.id);
      if (rotated) reveal(rotated);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSave = async (id: string, update: { title: string; scope: McpAccessScope }) => {
    if (!editProfile) return;
    const scopeChanged = update.scope !== editProfile.scope;
    // Defense in depth (mirrors handleRotateConfirm): a scope change re-mints via rotate, so refuse it
    // when either the stored or target scope is one this role can't hold. Unreachable through the gated
    // UI (admins can't reach a scope change, engineers hold both scopes) — parity guards the store call.
    if (scopeChanged && ![editProfile.scope, update.scope].every((scope) => allowedScopes.includes(scope))) {
      return;
    }
    setSubmitting(true);
    try {
      if (scopeChanged) {
        // Rotate first so a scope change always mints a fresh token and can never leave an
        // already-distributed token valid at the new scope, even if the scope update then fails.
        const rotated = await rotateMcpAccessProfile(id);
        if (!rotated) return;
        const updated = await updateMcpAccessProfile(id, update);
        setEditProfile(null);
        if (updated) {
          reveal({ ...updated, token: rotated.token });
        } else {
          reveal(
            rotated,
            'Your token was rotated, but the scope change could not be saved. The new token keeps the previous scope.'
          );
        }
      } else {
        const updated = await updateMcpAccessProfile(id, update);
        if (updated) setEditProfile(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    setSubmitting(true);
    try {
      await revokeMcpAccessProfile(revokeTarget.id);
    } finally {
      setSubmitting(false);
    }
  };

  if (allowedScopes.length === 0) return null;

  return (
    <div
      data-testid="mcp-access-section"
      className="space-y-4">
      <div className="space-y-1">
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          MCP Access Tokens
        </TextBlock>
        <TextBlock
          size="sm"
          color="grey6">
          Create and manage personal tokens for connecting Claude Code and other MCP clients to Tally.
        </TextBlock>
      </div>

      <McpAccessCreateForm
        onCreate={handleCreate}
        allowedScopes={allowedScopes}
        submitting={submitting}
      />

      {listError && (
        <Alert
          severity="error"
          data-testid="mcp-access-list-error"
          action={
            <button
              type="button"
              className="text-sm font-semibold text-indigo"
              onClick={() => fetchMcpAccessProfiles()}>
              Try again
            </button>
          }>
          We couldn&apos;t load your MCP access tokens.
        </Alert>
      )}

      <McpAccessProfilesTable
        profiles={profiles}
        allowedScopes={allowedScopes}
        loading={loading}
        hasError={!!listError}
        actionsDisabled={submitting}
        onRotate={setRotateTarget}
        onEdit={setEditProfile}
        onRevoke={setRevokeTarget}
      />

      <McpAccessTokenRevealModal
        open={!!revealProfile}
        profile={revealProfile}
        warning={revealNotice}
        onClose={closeReveal}
      />

      <McpAccessEditDialog
        open={!!editProfile}
        profile={editProfile}
        allowedScopes={allowedScopes}
        onClose={() => setEditProfile(null)}
        onSave={handleEditSave}
        submitting={submitting}
      />

      <ConfirmationModal
        open={!!rotateTarget}
        setOpen={(state) => {
          if (!state) setRotateTarget(null);
        }}
        handleSubmit={handleRotateConfirm}
        disabled={submitting}
        title="Rotate access token"
        text={
          rotateTarget
            ? `Rotate "${rotateTarget.title}"? The current token stops working immediately. A new token is shown once.`
            : ''
        }
        confirmButtonText="Rotate"
      />

      <ConfirmationModal
        open={!!revokeTarget}
        setOpen={(state) => {
          if (!state) setRevokeTarget(null);
        }}
        handleSubmit={handleRevokeConfirm}
        disabled={submitting}
        title="Revoke access token"
        text={
          revokeTarget
            ? `Revoke "${revokeTarget.title}"? Any client using this token will stop working immediately.`
            : ''
        }
        confirmButtonText="Revoke"
      />
    </div>
  );
};
